const OUTER_DURATION = 3000
const INNER_DURATION = 2000

const STYLES = `
  *, *::before, *::after { box-sizing: border-box; }

  :host {
    display: block;
    width: 100%;
    height: 100%;
    min-height: 120px;
    overflow: hidden;
  }

  :host(:only-child) {
    height: 100vh;
    height: 100dvh;
  }

  :host([no-bg]) .container { background: transparent; }

  .container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    /* content-background flips with both OS and a forced w-theme-* scope
       (inherited token), so the overlay follows a forced theme too. */
    background: var(--p-content-background, #fff);
    font-family: system-ui, -apple-system, sans-serif;
  }

  .spinner {
    position: relative;
    width: 48px;
    height: 48px;
    margin-bottom: 16px;
  }

  .ring {
    position: absolute;
    border-radius: 50%;
    box-sizing: border-box;
  }

  .ring.outer {
    width: 48px;
    height: 48px;
    border: 3px solid var(--p-surface-300, #d4d4d8);
    animation: spin-outer ${OUTER_DURATION}ms linear infinite;
  }

  .ring.inner {
    width: 56px;
    height: 56px;
    top: 50%;
    left: 50%;
    border: 3px solid transparent;
    border-top-color: var(--p-primary, rgb(0, 95, 178));
    border-bottom-color: var(--p-primary, rgb(0, 95, 178));
    animation: spin-inner ${INNER_DURATION}ms linear infinite;
  }

  .title {
    font-size: 14px;
    font-weight: 500;
    color: var(--p-text-color, #3f3f46);
    text-align: center;
    padding: 0 16px;
  }

  .title:empty { display: none; }

  .subtitle {
    font-size: 12px;
    color: var(--p-text-muted-color, #71717a);
    margin-top: 4px;
    text-align: center;
    padding: 0 16px;
  }

  .subtitle:empty { display: none; }

  /* Before the theme tokens load, follow the color-scheme the page sets.
     The facade puts the app's forced theme on <html> (color-scheme and
     .w-theme-dark) before this overlay paints, and color-scheme inherits
     into this shadow root, so light-dark() picks the app theme even when
     the OS preference differs. Supported in Chromium 123+, Firefox 120+
     and Safari 17.5+; older browsers skip this block and keep the plain
     fallbacks above. */
  @supports (color: light-dark(#fff, #000)) {
    .container {
      background: var(--p-content-background, light-dark(#fff, #18181b));
    }
    .ring.outer {
      border-color: light-dark(var(--p-surface-300, #d4d4d8), var(--p-surface-500, #71717a));
    }
    .ring.inner {
      border-top-color: light-dark(var(--p-primary, rgb(0, 95, 178)), var(--p-primary, rgb(0, 125, 178)));
      border-bottom-color: light-dark(var(--p-primary, rgb(0, 95, 178)), var(--p-primary, rgb(0, 125, 178)));
    }
    .title {
      color: var(--p-text-color, light-dark(#3f3f46, #fff));
    }
    .subtitle {
      color: var(--p-text-muted-color, light-dark(#71717a, #a1a1aa));
    }
  }

  /* Pages that set no color-scheme resolve light-dark() to light, so the
     OS preference still decides for them. Comes after the block above. A
     page that forces light while the OS prefers dark still gets the dark
     fallbacks: CSS cannot tell a forced light scheme from an unset one. */
  @media (prefers-color-scheme: dark) {
    .container {
      background: var(--p-content-background, #18181b);
    }
    .ring.outer {
      border-color: var(--p-surface-500, #71717a);
    }
    .ring.inner {
      border-top-color: var(--p-primary, rgb(0, 125, 178));
      border-bottom-color: var(--p-primary, rgb(0, 125, 178));
    }
    .title {
      color: var(--p-text-color, #fff);
    }
    .subtitle {
      color: var(--p-text-muted-color, #a1a1aa);
    }
  }

  @keyframes spin-outer {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  @keyframes spin-inner {
    from { transform: translate(-50%, -50%) rotate(0deg); }
    to { transform: translate(-50%, -50%) rotate(-360deg); }
  }
`

export class WippyLoadingElement extends HTMLElement {
  static observedAttributes = ['title', 'subtitle']

  private _titleEl: HTMLElement | null = null
  private _subtitleEl: HTMLElement | null = null

  connectedCallback() {
    const shadow = this.shadowRoot ?? this.attachShadow({ mode: 'open' })
    shadow.textContent = ''

    const style = document.createElement('style')
    style.textContent = STYLES
    shadow.appendChild(style)

    const container = document.createElement('div')
    container.className = 'container'

    const spinner = document.createElement('div')
    spinner.className = 'spinner'

    const outer = document.createElement('div')
    outer.className = 'ring outer'

    const inner = document.createElement('div')
    inner.className = 'ring inner'

    spinner.append(outer, inner)

    this._titleEl = document.createElement('div')
    this._titleEl.className = 'title'
    this._titleEl.setAttribute('part', 'title')

    this._subtitleEl = document.createElement('div')
    this._subtitleEl.className = 'subtitle'
    this._subtitleEl.setAttribute('part', 'subtitle')

    this._updateText()

    container.append(spinner, this._titleEl, this._subtitleEl)
    shadow.appendChild(container)

    // Time-synced animation — phase matches wall-clock time
    const now = Date.now()
    outer.style.animationDelay = `-${now % OUTER_DURATION}ms`
    inner.style.animationDelay = `-${now % INNER_DURATION}ms`
  }

  disconnectedCallback() {
    this._titleEl = null
    this._subtitleEl = null
  }

  attributeChangedCallback() {
    this._updateText()
  }

  private _updateText() {
    if (this._titleEl)
      this._titleEl.textContent = this.getAttribute('title') ?? ''
    if (this._subtitleEl)
      this._subtitleEl.textContent = this.getAttribute('subtitle') ?? ''
  }
}
